import { useEffect, useMemo, useState } from "react";
import apiClient from "../../../../services/api";
import { mapScoping } from "../../../../mappers";
import "../../audits/styles/audits.css";
import {
  C,
  SCOPING_STAGES,
  SCOPING_STAGE_TEAM,
} from "../../../../constants";
import { getInit, relTime, uid } from "../../../../utils";
import RichCommentInput from "../../../../shared/comments/RichCommentInput";
import { CommentContent } from "../../../../shared/comments/commentContent";
import {
  monthsBetween,
  computeBudgetHours,
  computeProjectBudget,
  computeProjectCost,
  computeMarginPct,
} from "../../../../lib/financials";
import { SheetPrimitives, Timeline } from "../../../../shared/ui";
import { cn } from "../../../../lib/cn";

const CURRENCY_OPTIONS = [
  { value: "SEK", label: "SEK" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
];

const SCOPING_STAGE_LABEL_TO_ENUM = {
  "1. New": "NEW",
  "2. Customer Info": "CUSTOMER_INFO",
  "3. Estimation": "ESTIMATION",
  "4. Proposal": "PROPOSAL",
  "5. Developer Matching": "DEVELOPER_MATCHING",
  "6. Meeting": "MEETING",
  "7. Done": "DONE",
  "8. Lost": "LOST",
};

const SCOPING_STAGE_ENUM_TO_LABEL = Object.fromEntries(Object.entries(SCOPING_STAGE_LABEL_TO_ENUM).map(([label, value]) => [value, label]));

const SCOPING_STAGE_TASK_TEMPLATES = {
  "1. New": [
    { title: "Validate discovery for {name}", dept: "Sales", description: "Confirm context, stakeholders, and business drivers." },
    { title: "Log {name} in CRM", dept: "Sales", description: "Capture lead status, referral, and contact details." },
  ],
  "2. Customer Info": [
    { title: "Document customer intelligence for {name}", dept: "Sales", description: "Include org structure, systems, and risks." },
    { title: "Map success criteria with {name}", dept: "Sales", description: "Agree what success looks like and capture timeline." },
  ],
  "3. Estimation": [
    { title: "Build estimation for {name}", dept: "Delivery", description: "Translate scope into effort, milestones, and roles." },
    { title: "Review risk & assumptions for {name}", dept: "Delivery", description: "Document dependencies and client expectations." },
  ],
  "4. Proposal": [
    { title: "Draft proposal for {name}", dept: "Sales", description: "Include commercial terms, deliverables, and team." },
    { title: "Send proposal to {name} stakeholders", dept: "Sales", description: "Track feedback and decision timeline." },
  ],
  "5. Developer Matching": [
    { title: "Define candidate profile for {name}", dept: "Recruitment", description: "List skills, seniority, rate, and experience requirements." },
    { title: "Shortlist bench talent for {name}", dept: "Recruitment", description: "Gather CVs/availability and align on fit." },
  ],
  "6. Meeting": [
    { title: "Coordinate alignment meeting for {name}", dept: "Sales", description: "Set agenda, attendees, and desired outcomes." },
  ],
  "7. Done": [
    { title: "Hand off {name} to delivery", dept: "Operations", description: "Share docs, kickoff plan, and assigned consultants." },
  ],
  "8. Lost": [
    { title: "Capture loss context for {name}", dept: "Sales", description: "Log reasons, blockers, and future re-engagement plan." },
  ],
};

export default function ScopingPage({scoping, setScoping, consultants, nav, showToast, currentUser, AvatarComponent}){
  const CURRENT_USER = currentUser;
  const Avt = AvatarComponent;
  const [selectedCard,setSelectedCard]=useState(null);
  const [newTaskText,setNewTaskText]=useState("");
  const [showDoneLost,setShowDoneLost]=useState(false);
  const [editorOpen,setEditorOpen]=useState(false);
  const [editorMode,setEditorMode]=useState("create");
  const [editorItemId,setEditorItemId]=useState(null);
  const [editorForm,setEditorForm]=useState(null);
  const [editorTouched,setEditorTouched]=useState({});
  // BigClaude Phase 4: dedicated Financials drawer. Null = closed; otherwise
  // holds the scoping item id whose financials are being viewed. The drawer
  // reads the live item from `scoping` state so edits from any source
  // (Financials modal, Dev Matching row inputs, etc.) stay in sync.
  const [financialsOpenId,setFinancialsOpenId]=useState(null);
  const [draggingId,setDraggingId]=useState(null);
  const [dragOverStage,setDragOverStage]=useState(null);
  const [expandedTaskId,setExpandedTaskId]=useState(null);
  const [expandedSubtaskId,setExpandedSubtaskId]=useState(null);
  const [taskCommentDrafts,setTaskCommentDrafts]=useState({});
  const [subtaskCommentDrafts,setSubtaskCommentDrafts]=useState({});
  const [scopingCommentDraft,setScopingCommentDraft]=useState("");
  const [activeUsers,setActiveUsers]=useState([]);
  const [teams,setTeams]=useState([]);
  // Developer Matching state
  const [devMatchSearch,setDevMatchSearch]=useState("");
  const [showExternalForm,setShowExternalForm]=useState(false);
  const [extDevForm,setExtDevForm]=useState({name:"",skills:"",availableFrom:"",note:"",cvName:"",cvDataUrl:""});
  // Table view state
  const [viewMode,setViewMode]=useState("kanban"); // "kanban" | "table"
  const [expandedTableGroups,setExpandedTableGroups]=useState({});
  const [expandedTableItems,setExpandedTableItems]=useState({});
  const [updatePanelId,setUpdatePanelId]=useState(null);
  const [updatePanelText,setUpdatePanelText]=useState("");
  const [drawerTab,setDrawerTab]=useState("overview");

  useEffect(() => {
    apiClient.getActiveUsers().then(d=>{
      const list = Array.isArray(d.users) ? d.users : Array.isArray(d) ? d : [];
      setActiveUsers(list);
    }).catch(err=>{
      console.error("Failed to load active users", err);
      setActiveUsers([]);
    });
    apiClient.getTeams().then(d=>{
      const list = Array.isArray(d.teams) ? d.teams : [];
      setTeams(list.map(team=>({
        id: team.id,
        name: team.name,
        stage: team.stage,
        members: Array.isArray(team.members) ? team.members : [],
        stageLabel: SCOPING_STAGE_ENUM_TO_LABEL[team.stage]||team.stage,
      })));
    }).catch(err=>{
      console.error("Failed to load teams", err);
      setTeams([]);
    });
  }, []);

  const stageTeamMap = useMemo(() => {
    const map = {};
    teams.forEach(team => {
      if(team?.stage) map[team.stage] = team;
    });
    return map;
  }, [teams]);

  const getTeamForStage = stageLabel => {
    const enumKey = SCOPING_STAGE_LABEL_TO_ENUM[stageLabel] || stageLabel;
    return stageTeamMap[enumKey];
  };

  const buildStageTasks = (stageLabel, team, context) => {
    const templates = SCOPING_STAGE_TASK_TEMPLATES[stageLabel] || [];
    if(!templates.length) return [];
    // Extract full user objects (id + name) from team members for dynamic assignment
    const members = (team?.members || [])
      .map(m=>({ id: m.user?.id || null, name: m.user?.name || null }))
      .filter(m=>m.name);
    const fallbackName = context.ownerName || SCOPING_STAGE_TEAM[stageLabel]?.defaultAssignee || "Team";
    const projectName = context.projectName || "this opportunity";
    return templates.map((template, index) => {
      const member = members.length ? members[index % members.length] : null;
      return {
        id: `stask_${stageLabel.replace(/\W/g,"")}_${Date.now()}_${index}`,
        title: template.title.replace("{name}", projectName),
        description: template.description?.replace("{name}", projectName) || "",
        dept: template.dept,
        assignee: member?.name || fallbackName,
        assigneeId: member?.id || null,
        done: false,
        comments: [],
        attachments: [],
        metaStage: stageLabel,
      };
    });
  };

  const ensureStageTasks = (existingTasks, stageLabel, team, context) => {
    if(!stageLabel) return [];
    const already = (existingTasks||[]).some(task=>task.metaStage===stageLabel);
    if(already) return [];
    return buildStageTasks(stageLabel, team, context);
  };

  const MONTH_OPTIONS=["January","February","March","April","May","June","July","August","September","October","November","December"];
  const usersMap=useMemo(()=>Object.fromEntries(activeUsers.map(u=>[u.id,u])),[activeUsers]);

  // Stats
  const totalActive=scoping.filter(s=>!s.stage.includes("Done")&&!s.stage.includes("Lost")).length;
  const inMatching=scoping.filter(s=>s.stage==="5. Developer Matching").length;
  const won=scoping.filter(s=>s.stage==="7. Done").length;
  const lost=scoping.filter(s=>s.stage==="8. Lost").length;

  // Stage colors — keyed by label. Each stage keeps the color/icon of its
  // semantic identity (Proposal=orange, Developer Matching=amber) regardless
  // of numeric position, so reordering the workflow preserves recognition.
  const STAGE_COLORS={
    "1. New":"#60a5fa",
    "2. Customer Info":"#a78bfa",
    "3. Estimation":"#ec4899",
    "4. Proposal":"#f97316",
    "5. Developer Matching":"#f59e0b",
    "6. Meeting":"#14b8a6",
    "7. Done":"#10c981",
    "8. Lost":"#6b7280",
  };
  const STAGE_ICONS={"1. New":"★","2. Customer Info":"◈","3. Estimation":"◬","4. Proposal":"▣","5. Developer Matching":"◇","6. Meeting":"◉","7. Done":"✓","8. Lost":"✗"};
  const PRIORITY_STYLE={
    Critical:{fg:"var(--rc-red)",bg:"rgba(239,68,68,.14)"},
    High:{fg:"var(--rc-yellow)",bg:"rgba(245,158,11,.16)"},
    Medium:{fg:"var(--rc-blue-steel)",bg:"rgba(84,129,202,.16)"},
    Low:{fg:"var(--rc-green-secondary)",bg:"rgba(94,107,100,.16)"},
  };

  // Priority sort: Critical > High > Medium > none > Low
  const PRIO_ORDER={Critical:0,High:1,Medium:2,Low:4};
  const sortCards=(a,b)=>(PRIO_ORDER[a.priority]??2)-(PRIO_ORDER[b.priority]??2);

  // Visible stages (collapse Done/Lost by default)
  const activeStages=SCOPING_STAGES.filter(s=>!s.includes("Done")&&!s.includes("Lost"));
  const endStages=SCOPING_STAGES.filter(s=>s.includes("Done")||s.includes("Lost"));

  const todayIso=()=>new Date().toISOString().slice(0,10);
  const activeCard=useMemo(()=>selectedCard?scoping.find(s=>s.id===selectedCard.id)||selectedCard:null,[scoping,selectedCard]);

  useEffect(() => {
    if (selectedCard?.id) {
      setDrawerTab("overview");
      setTaskCommentDrafts({});
      setSubtaskCommentDrafts({});
      setScopingCommentDraft("");
    }
  }, [selectedCard?.id]);

  function toScopingApiPayload(item){
    const matching=item.matching||{};
    return {
      name:item.name,
      owner:item.owner||null,
      ownerId:item.ownerId||null,
      stage:item.stage,
      priority:item.priority||null,
      track:item.track||null,
      probability:item.probability ?? null,
      dealValue:item.dealValue ?? null,
      months:item.months||[],
      earliestDueDate:item.earliestDueDate || null,
      leadActive:item.leadActive||null,
      startDate:item.startDate || null,
      endDate:item.endDate || null,
      description:item.description||"",
      assignedTeamId:item.assignedTeam?.id||null,
      collaboratorIds:item.collaboratorIds||[],
      matchNotes:matching.matchNotes||"",
      stageHistory:item.stageHistory||[],
    };
  }

  function upsertScopingLocal(mapped, select=true){
    setScoping(sc=>{
      const exists=sc.some(item=>item.id===mapped.id);
      return exists ? sc.map(item=>item.id===mapped.id?mapped:item) : [mapped,...sc];
    });
    if(select) setSelectedCard(mapped);
  }

  function removeScopingLocal(id){
    setScoping(sc=>sc.filter(item=>item.id!==id));
    setSelectedCard(prev=>prev?.id===id?null:prev);
  }

  function getStageDefaultOwner(stage="1. New"){
    const stInfo=SCOPING_STAGE_TEAM[stage]||{};
    return {
      ownerId: CURRENT_USER.id || "",
      ownerName: CURRENT_USER.name || stInfo.defaultAssignee || "",
    };
  }

  function emptyScopingForm(stage="1. New"){
    const { ownerId, ownerName } = getStageDefaultOwner(stage);
    const stageTeam = getTeamForStage(stage);
    return {
      name:"",
      owner:ownerName,
      ownerId:ownerId,
      assignedTeamId:stageTeam?.id||"",
      stage,
      priority:"",
      track:"",
      probability:"",
      dealValue:"",
      months:[],
      earliestDueDate:"",
      leadActive:"",
      startDate:"",
      endDate:"",
      description:"",
      collaborators:[],
      matchNotes:"",
    };
  }

  function formFromItem(item){
    return {
      name:item.name||"",
      owner:item.owner||"",
      ownerId:item.ownerId||"",
      assignedTeamId:item.assignedTeam?.id||"",
      stage:item.stage||"1. New",
      priority:item.priority||"",
      track:item.track||"",
      probability:item.probability ?? "",
      dealValue:item.dealValue ?? "",
      months:Array.isArray(item.months)?item.months:[],
      earliestDueDate:item.earliestDueDate||"",
      leadActive:item.leadActive||"",
      startDate:item.startDate||"",
      endDate:item.endDate||"",
      description:item.description||"",
      collaborators:Array.isArray(item.collaborators)?item.collaborators.map(c=>c.id):[],
      matchNotes:item.matching?.matchNotes||"",
    };
  }

  function openCreateModal(stage){
    setEditorMode("create");
    setEditorItemId(null);
    setEditorForm(emptyScopingForm(stage));
    setEditorTouched({});
    setEditorOpen(true);
  }

  function openEditModal(item){
    setEditorMode("edit");
    setEditorItemId(item.id);
    setEditorForm(formFromItem(item));
    // Mark every required field as touched on edit so missing values are
    // flagged immediately — a PM who opens an auto-created scoping can see
    // exactly what still needs filling before Save.
    setEditorTouched({name:true, ownerId:true, stage:true, startDate:true, endDate:true});
    setEditorOpen(true);
  }

  function closeEditor(){
    setEditorOpen(false);
    setEditorItemId(null);
    setEditorForm(null);
    setEditorTouched({});
  }

  function setEditorField(field,value){
    setEditorTouched(t=>({...t,[field]:true}));
    setEditorForm(f=>{
      const next={...f,[field]:value};
      if(field==="stage"){
        const stageTeam=getTeamForStage(value);
        next.assignedTeamId=stageTeam?.id||null;
        // When the stage changes and no owner has been picked yet, roll
        // forward the stage's default owner so the Owner field stays
        // populated without extra clicks.
        if(!next.ownerId){
          const def = getStageDefaultOwner(value);
          if(def?.ownerId){ next.ownerId = def.ownerId; next.owner = def.ownerName; }
        }
      }
      return next;
    });
  }

  function toggleEditorListValue(field,value){
    setEditorForm(f=>({
      ...f,
      [field]:f[field].includes(value)?f[field].filter(v=>v!==value):[...f[field],value],
    }));
  }

  // Scoping fields required on every manual create/edit. Auto-created
  // scopings (Lead→Scoping, Audit→Scoping) bypass this because they go
  // through `createScopingFromSource`, not this modal.
  const SCOPING_REQUIRED = [
    { key: "name",      label: "Name",       check: (v) => !!String(v||"").trim() },
    { key: "ownerId",   label: "Owner",      check: (v) => !!v },
    { key: "stage",     label: "Stage",      check: (v) => !!v },
    { key: "startDate", label: "Start Date", check: (v) => !!v },
    { key: "endDate",   label: "End Date",   check: (v) => !!v },
  ];
  const editorErrors = editorForm
    ? SCOPING_REQUIRED
        .filter((r) => !r.check(editorForm[r.key]))
        .reduce((acc, r) => ({ ...acc, [r.key]: `${r.label} is required` }), {})
    : {};
  const hasEditorError = (field) => editorTouched[field] ? editorErrors[field] : undefined;

  async function saveEditor(){
    // Mark every required field as touched so the hints render, then
    // block submission if anything's missing.
    setEditorTouched(Object.fromEntries(SCOPING_REQUIRED.map((r) => [r.key, true])));
    const missing = SCOPING_REQUIRED.filter((r) => !r.check(editorForm?.[r.key]));
    if(missing.length){
      showToast?.("Incomplete scoping", `Missing: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    const now=todayIso();
    const stInfo=SCOPING_STAGE_TEAM[editorForm.stage]||{};
    const ownerName = usersMap[editorForm.ownerId]?.name || editorForm.owner || stInfo.defaultAssignee || "";
    const stageTeam = getTeamForStage(editorForm.stage);
    const stageContext = { projectName: editorForm.name.trim(), ownerName };
    const basePayload={
      name:editorForm.name.trim(),
      owner:ownerName,
      ownerId:editorForm.ownerId||null,
      stage:editorForm.stage,
      priority:editorForm.priority||null,
      track:editorForm.track||null,
      probability:editorForm.probability===""?null:Number(editorForm.probability),
      dealValue:editorForm.dealValue===""?null:Number(editorForm.dealValue),
      months:editorForm.months||[],
      earliestDueDate:editorForm.earliestDueDate||null,
      leadActive:editorForm.leadActive||null,
      startDate:editorForm.startDate||"",
      endDate:editorForm.endDate||"",
      description:editorForm.description.trim(),
      collaborators:editorForm.collaborators||[],
      assignedTeamId:editorForm.assignedTeamId||null,
      matching:{ matchNotes:(editorForm.matchNotes||"").trim() },
    };

    try{
      if(editorMode==="create"){
        const newItem={
          ...basePayload,
          stageHistory:[{stage:editorForm.stage,enteredAt:now,completedAt:null,by:ownerName||CURRENT_USER.name}],
        };
        const created=await apiClient.createScoping(toScopingApiPayload(newItem));
        const mapped=mapScoping(created);
        upsertScopingLocal(mapped,true);
        // Create stage tasks via atomic API
        const stageTasks=buildStageTasks(basePayload.stage, stageTeam, stageContext);
        if(stageTasks.length){
          const withTasks=await apiClient.batchCreateScopingTasks(created.id, stageTasks.map(t=>({
            title:t.title, description:t.description||"", phase:stageContext.projectName||t.metaStage||basePayload.stage,
            assigneeId:t.assigneeId||null,
          })));
          upsertScopingLocal(mapScoping(withTasks),true);
        }
        showToast?.("Scoping item created", mapped.name);
        closeEditor();
        return;
      }

      const current=scoping.find(item=>item.id===editorItemId);
      if(!current) return;
      const stageChanged=current.stage!==editorForm.stage;
      const history=[...(current.stageHistory||[])];
      if(stageChanged){
        const lastEntry=history[history.length-1];
        if(lastEntry && !lastEntry.completedAt) lastEntry.completedAt=now;
        history.push({stage:editorForm.stage,enteredAt:now,completedAt:null,by:ownerName||CURRENT_USER.name});
      }
      const updatedItem={
        ...current, ...basePayload,
        stageHistory:stageChanged?history:(current.stageHistory||[]),
      };
      const updated=await apiClient.updateScoping(editorItemId,toScopingApiPayload(updatedItem));
      const mapped=mapScoping(updated);
      upsertScopingLocal(mapped,true);
      // Append new stage tasks if stage changed
      if(stageChanged){
        const existing=mapped.tasks||[];
        const stageTasks=ensureStageTasks(existing, basePayload.stage, stageTeam, stageContext);
        if(stageTasks.length){
          const withTasks=await apiClient.batchCreateScopingTasks(editorItemId, stageTasks.map(t=>({
            title:t.title, description:t.description||"", phase:basePayload.stage,
            assigneeId:t.assigneeId||null,
          })));
          upsertScopingLocal(mapScoping(withTasks),true);
        }
      }
      showToast?.("Scoping item updated", mapped.name);
      closeEditor();
    }catch(err){
      console.error("Failed to save scoping item",err);
      showToast?.("Error","Failed to save scoping item");
    }
  }

  // Move card to new stage
  async function moveToStage(cardId,newStage){
    const item=scoping.find(s=>s.id===cardId);
    if(!item) return;
    const now=todayIso();
    const updatedHistory=[...(item.stageHistory||[])];
    const lastEntry=updatedHistory[updatedHistory.length-1];
    if(lastEntry&&!lastEntry.completedAt) lastEntry.completedAt=now;
    const stInfo=SCOPING_STAGE_TEAM[newStage]||{};
    updatedHistory.push({stage:newStage,enteredAt:now,completedAt:null,by:stInfo.defaultAssignee||item.owner||CURRENT_USER.name});
    const stageTeam=getTeamForStage(newStage);
    const stageContext={projectName:item.name,ownerName:item.owner||CURRENT_USER.name};
    // Done-stage hook — reserved for future integration (e.g. CRM handoff, notification webhook)
    // if(newStage==="7. Done") await triggerDoneIntegration(nextItem);
    try{
      const updated=await apiClient.updateScoping(cardId,toScopingApiPayload({
        ...item, stage:newStage, stageHistory:updatedHistory,
        assignedTeamId: stageTeam?.id||null,
      }));
      const mapped=mapScoping(updated);
      upsertScopingLocal(mapped,true);
      // Append new stage tasks via atomic API
      const stageTasks=ensureStageTasks(mapped.tasks||[], newStage, stageTeam, stageContext);
      if(stageTasks.length){
        const withTasks=await apiClient.batchCreateScopingTasks(cardId, stageTasks.map(t=>({
          title:t.title, description:t.description||"", phase:newStage,
          assigneeId:t.assigneeId||null,
        })));
        upsertScopingLocal(mapScoping(withTasks),true);
      }
      showToast?.("Stage updated", `Moved to ${newStage.replace(/^\d\.\s/,"")}`);
    }catch(err){
      console.error("Failed to move scoping stage",err);
      showToast?.("Error","Failed to move scoping stage");
    }
  }

  // Toggle task done — atomic
  async function toggleTask(cardId,taskId){
    const item=scoping.find(s=>s.id===cardId);
    if(!item) return;
    const task=(item.tasks||[]).find(t=>t.id===taskId);
    if(!task) return;
    // Optimistic update
    upsertScopingLocal(mapScoping({...item,scopingTasks:(item.tasks||[]).map(t=>t.id===taskId?{...t,done:!t.done}:t)}),false);
    try{
      const updated=await apiClient.toggleScopingTaskDone(taskId,!task.done);
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to toggle scoping task",err);
      showToast?.("Error","Failed to update task");
    }
  }

  // Add task to card — atomic
  async function addTask(cardId,title){
    if(!title.trim())return;
    try{
      const updated=await apiClient.createScopingTask(cardId,{title:title.trim(),phase:"scoping"});
      upsertScopingLocal(mapScoping(updated),false);
      setNewTaskText("");
      showToast?.("Task added", title.trim());
    }catch(err){
      console.error("Failed to add scoping task",err);
      showToast?.("Error","Failed to add task");
    }
  }

  async function removeTask(cardId,taskId){
    try{
      const updated=await apiClient.deleteScopingTask(taskId);
      upsertScopingLocal(mapScoping(updated),false);
      showToast?.("Task removed","Scoping task deleted");
    }catch(err){
      console.error("Failed to remove scoping task",err);
      showToast?.("Error","Failed to remove task");
    }
  }

  // Assign task — atomic
  async function assignTask(taskId,assigneeId){
    try{
      const updated=await apiClient.assignScopingTask(taskId,assigneeId||null);
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to assign scoping task",err);
      showToast?.("Error","Failed to assign task");
    }
  }

  // Toggle subtask done — atomic
  async function toggleSubtask(subtaskId,currentDone){
    try{
      const updated=await apiClient.toggleScopingSubtaskDone(subtaskId,!currentDone);
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to toggle subtask",err);
      showToast?.("Error","Failed to update subtask");
    }
  }

  // Add subtask — atomic
  async function addSubtask(taskId,title){
    if(!title.trim()) return;
    try{
      const updated=await apiClient.createScopingSubtask(taskId,{title:title.trim()});
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to add subtask",err);
      showToast?.("Error","Failed to add subtask");
    }
  }

  // Remove subtask — atomic
  async function removeSubtask(subtaskId){
    try{
      const updated=await apiClient.deleteScopingSubtask(subtaskId);
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to remove subtask",err);
      showToast?.("Error","Failed to remove subtask");
    }
  }

  // Assign subtask — atomic
  async function assignSubtask(subtaskId,assigneeId){
    try{
      const updated=await apiClient.updateScopingSubtask(subtaskId,{assigneeId:assigneeId||null});
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to assign subtask",err);
    }
  }

  // Set due date on task — atomic with optimistic update
  async function setTaskDueDate(taskId,dueDate){
    const normalized=dueDate||null;
    // Optimistic: update local state immediately so the controlled input doesn't revert
    setScoping(sc=>sc.map(card=>({
      ...card,
      tasks:(card.tasks||[]).map(t=>t.id===taskId?{...t,dueDate:normalized}:t),
    })));
    try{
      const updated=await apiClient.updateScopingTask(taskId,{dueDate:normalized});
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to set task due date",err);
      showToast?.("Error","Failed to save due date");
    }
  }

  // Set due date on subtask — atomic with optimistic update
  async function setSubtaskDueDate(subtaskId,dueDate){
    const normalized=dueDate||null;
    // Optimistic update
    setScoping(sc=>sc.map(card=>({
      ...card,
      tasks:(card.tasks||[]).map(t=>({
        ...t,
        subtasks:(t.subtasks||[]).map(st=>st.id===subtaskId?{...st,dueDate:normalized}:st),
      })),
    })));
    try{
      const updated=await apiClient.updateScopingSubtask(subtaskId,{dueDate:normalized});
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to set subtask due date",err);
      showToast?.("Error","Failed to save due date");
    }
  }

  // Save matched developers for a scoping item
  async function saveMatchedDevelopers(itemId,matchedDevelopers){
    // Optimistic update — show changes immediately
    setScoping(sc=>sc.map(card=>card.id===itemId?{
      ...card,
      matching:{...card.matching,matchedDevelopers}
    }:card));
    try{
      const updated=await apiClient.updateScoping(itemId,{matchedDevelopers});
      upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to save matched developers",err);
      showToast?.("Error","Failed to save developer matches");
    }
  }

  // Per-field save for the Financials drawer. Accepts a patch object so the
  // caller can batch multiple fields in one request (e.g. start/end dates
  // committed together). Mirrors the optimistic pattern used above.
  async function saveScopingFields(itemId, patch) {
    setScoping(sc => sc.map(card => card.id === itemId ? { ...card, ...patch } : card));
    try {
      const updated = await apiClient.updateScoping(itemId, patch);
      upsertScopingLocal(mapScoping(updated), false);
    } catch (err) {
      console.error("Failed to save scoping field", err);
      showToast?.("Error", err?.response?.data?.error || "Failed to save");
    }
  }

  async function addTaskComment(taskId,input=taskCommentDrafts[taskId] || ""){
    const payload=typeof input==="string"?{content:input}:input;
    if(!payload?.content) return;
    try{
      const updated=await apiClient.addScopingTaskComment(taskId,payload);
      upsertScopingLocal(mapScoping(updated),false);
      setTaskCommentDrafts(prev=>({ ...prev, [taskId]: "" }));
      showToast?.("Comment added","");
    }catch(err){
      console.error("Failed to add task comment",err);
      showToast?.("Error","Failed to add comment");
    }
  }

  async function addSubtaskComment(subtaskId,input=subtaskCommentDrafts[subtaskId] || ""){
    const payload=typeof input==="string"?{content:input}:input;
    if(!payload?.content) return;
    try{
      const updated=await apiClient.addScopingSubtaskComment(subtaskId,payload);
      upsertScopingLocal(mapScoping(updated),false);
      setSubtaskCommentDrafts(prev=>({ ...prev, [subtaskId]: "" }));
      showToast?.("Comment added","");
    }catch(err){
      console.error("Failed to add subtask comment",err);
      showToast?.("Error","Failed to add comment");
    }
  }

  async function removeTaskAttachmentFile(attachmentId){
    try{
      const updated=await apiClient.deleteScopingTaskAttachment(attachmentId);
      if(updated) upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to delete task attachment",err);
      showToast?.("Error","Failed to delete file");
    }
  }

  async function removeSubtaskAttachmentFile(attachmentId){
    try{
      const updated=await apiClient.deleteScopingSubtaskAttachment(attachmentId);
      if(updated) upsertScopingLocal(mapScoping(updated),false);
    }catch(err){
      console.error("Failed to delete subtask attachment",err);
      showToast?.("Error","Failed to delete file");
    }
  }

  async function deleteScopingItem(id,name){
    try{
      await apiClient.deleteScoping(id);
      removeScopingLocal(id);
      showToast?.("Scoping item deleted", name || "Item removed");
    }catch(err){
      console.error("Failed to delete scoping item",err);
      showToast?.("Error","Failed to delete scoping item");
    }
  }

  // Render a single kanban card
  function KbCard({item}){
    const col=STAGE_COLORS[item.stage]||"#60a5fa";
    const pStyle=PRIORITY_STYLE[item.priority]||null;
    const pCol=pStyle?.fg||null;
    const tasks=item.tasks||[];
    const doneCount=tasks.filter(t=>t.done).length;
    // Use live team from DB; fall back to static stage-team map
    const liveTeam=getTeamForStage(item.stage);
    const teamName=item.assignedTeam?.name||liveTeam?.name||null;
    return (
      <div
        className={`kb-card${draggingId===item.id?" dragging":""}`}
        onClick={()=>setSelectedCard(item)}
        draggable
        onDragStart={e=>{setDraggingId(item.id); e.dataTransfer.effectAllowed="move"; e.dataTransfer.setData("text/plain", item.id);}}
        onDragEnd={()=>{setDraggingId(null);setDragOverStage(null);}}
      >
        {pStyle&&<div className="kb-card-prio" style={{borderColor:`transparent ${pStyle.bg} transparent transparent`}}/>}
        <div className="kb-card-name">
          {item.name}
          {item.priority&&<span className="kb-card-tag" style={{background:pStyle?.bg,color:pCol,fontSize:"9px"}}>{item.priority}</span>}
        </div>
        {item.description&&<div className="kb-card-desc">{item.description}</div>}
        {/* Live data chips: track, probability, deal value */}
        {(item.track||item.probability!=null||item.dealValue!=null||item.leadActive)&&(
          <div className="kb-card-data-row">
            {item.track&&<span className="kb-chip track">{item.track}</span>}
            {item.leadActive&&<span className="kb-chip lead-active">{item.leadActive}</span>}
            {item.probability!=null&&<span className="kb-chip prob">{item.probability}%</span>}
            {item.dealValue!=null&&<span className="kb-chip value">{Number(item.dealValue).toLocaleString()} kr</span>}
          </div>
        )}
        <div className="kb-card-meta">
          {item.stage==="5. Developer Matching"&&item.matching?.matchNotes&&(
            <span className="kb-card-tag" style={{background:"rgba(245,158,11,.12)",color:"#f59e0b"}}>◇ Matching</span>
          )}
          {item.collaborators?.length>0&&(
            <span className="kb-card-tag" style={{background:"rgba(96,165,250,.1)",color:"#60a5fa"}}>+{item.collaborators.length}</span>
          )}
          {teamName&&(
            <span className="kb-card-tag" style={{background:"rgba(59,127,250,.12)",color:"#60a5fa"}}>⊞ {teamName}</span>
          )}
          {item.owner&&<span className="kb-card-owner"><span className="kb-owner-dot" style={{background:col}}/>{item.owner.split(" ")[0]}</span>}
        </div>
        {tasks.length>0&&(
          <div className="kb-card-tasks">
            <span className="done">{doneCount}</span>/{tasks.length} tasks
            <span className="kb-card-progress">
              <span className="kb-card-progress-fill" style={{width:`${tasks.length>0?(doneCount/tasks.length*100):0}%`}}/>
            </span>
          </div>
        )}
      </div>
    );
  }

  // Render detail drawer
  function DetailDrawer(){
    if(!activeCard)return null;
    const item=activeCard;
    const col=STAGE_COLORS[item.stage]||"#60a5fa";
    const tasks=item.tasks||[];
    const history=item.stageHistory||[];
    const scopingComments = tasks
      .flatMap(task => (task.comments || []).map(comment => ({ ...comment, taskTitle: task.title })))
      .sort((a,b)=>new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
    const stInfo=SCOPING_STAGE_TEAM[item.stage]||{};

    return (
      <SheetPrimitives.Sheet open={!!activeCard} onOpenChange={(open)=>!open && setSelectedCard(null)}>
        <SheetPrimitives.SheetContent side="right" className="sm:max-w-[720px]">
          <SheetPrimitives.SheetHeader>
            <div className="flex flex-col gap-1.5 min-w-0 flex-1">
              <SheetPrimitives.SheetTitle>{item.name}</SheetPrimitives.SheetTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="kb-stage-pill" style={{background:col+"1a",color:col,border:`1px solid ${col}33`}}>
                  <span>{STAGE_ICONS[item.stage]}</span> {item.stage}
                  <span className="kb-stage-team-note">({stInfo.team} team)</span>
                </div>
                {item.priority && <span className="aud-type-badge">{item.priority}</span>}
                <span className="text-[11.5px] text-rc-text-secondary">{item.description||"No description"}</span>
              </div>
            </div>
          </SheetPrimitives.SheetHeader>

          {/* Tabs — match AuditsPage / LeadsPage pattern */}
          <div className="shrink-0 flex items-center gap-1 px-6 py-2 border-b border-rc-border bg-rc-surface">
            {[
              ["overview", "Overview", null],
              ["tasks", "Tasks", tasks.length || null],
              ["history", "History", history.length || null],
              ["comments", "Comments", scopingComments.length || null],
            ].map(([tabId, label, count]) => (
              <button
                key={tabId}
                onClick={()=>setDrawerTab(tabId)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-[12.5px] font-semibold cursor-pointer transition-colors",
                  "flex items-center gap-1.5",
                  drawerTab === tabId
                    ? "bg-[rgba(59,127,250,0.14)] text-rc-accent-strong"
                    : "text-rc-text-secondary hover:text-rc-text-primary",
                )}
              >
                {label}
                {count ? (
                  <span className={cn(
                    "font-mono text-[9.5px] px-1.5 rounded-[10px]",
                    drawerTab === tabId ? "bg-[rgba(59,127,250,0.25)] text-rc-accent-strong" : "bg-rc-border text-rc-text-secondary",
                  )}>{count}</span>
                ) : null}
              </button>
            ))}
          </div>

          <SheetPrimitives.SheetBody>
            {drawerTab==="overview" && (
              <>
            {/* Current stage + move buttons */}
            <div className="mt-5">
              <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Current Stage</div>
              <div className="kb-stage-nav">
                {SCOPING_STAGES.filter(st=>st!==item.stage).map(st=>{
                  const targetCol=STAGE_COLORS[st]||"#60a5fa";
                  return (
                  <button
                    key={st}
                    onClick={()=>moveToStage(item.id,st)}
                    style={{borderColor:`${targetCol}44`,color:targetCol,background:`${targetCol}14`}}
                  >
                    {STAGE_ICONS[st]} {st.replace(/^\d\.\s/,"")}
                  </button>
                )})}
              </div>
            </div>

            {/* Info grid */}
            <div className="mt-5">
              <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Details</div>
              <div className="kb-info-grid">
                <div className="kb-info-item"><div className="label">Owner</div>{item.owner||"Unassigned"}</div>
                <div className="kb-info-item"><div className="label">Priority</div>{item.priority||"None"}</div>
                <div className="kb-info-item"><div className="label">Start Date</div>{item.startDate||"—"}</div>
                <div className="kb-info-item"><div className="label">End Date</div>{item.endDate||"—"}</div>
                <div className="kb-info-item"><div className="label">Track</div>{item.track||"—"}</div>
                <div className="kb-info-item"><div className="label">Assigned Team</div>{item.assignedTeam?.name||stInfo.team||"—"}</div>
                <div className="kb-info-item"><div className="label">Lead / Active</div>{item.leadActive||"—"}</div>
                <div className="kb-info-item">
                  <div className="label">Collaborators</div>
                  {item.collaborators?.length
                    ? item.collaborators.map(c=>c.name).join(", ")
                    : "None"}
                </div>
                <div className="kb-info-item"><div className="label">Probability</div>{item.probability!=null?`${item.probability}%`:"—"}</div>
                <div className="kb-info-item"><div className="label">Value</div>{item.dealValue?`${item.dealValue.toLocaleString()} kr`:"—"}</div>
                <div className="kb-info-item"><div className="label">Month(s)</div>{item.months?.length?item.months.join(", "):"—"}</div>
                <div className="kb-info-item"><div className="label">Earliest Due</div>{item.earliestDueDate||"Not set"}</div>
                <div className="kb-info-item"><div className="label">Default Assignee</div>{stInfo.defaultAssignee||"—"}</div>
              </div>
            </div>

            {/* Developer Matching — only visible when item is in the Developer Matching stage */}
            {item.stage==="5. Developer Matching"&&(()=>{
              const matched=Array.isArray(item.matching?.matchedDevelopers)?item.matching.matchedDevelopers:[];
              const filteredConsultants=(consultants||[]).filter(c=>{
                const q=devMatchSearch.toLowerCase();
                if(!q) return true;
                return c.name?.toLowerCase().includes(q)||(c.skills||[]).some(s=>s.toLowerCase().includes(q));
              });
              function addConsultantMatch(c){
                const already=matched.some(m=>m.id===c.consultantId);
                if(already) return;
                const dev={
                  id:c.consultantId,
                  name:c.name,
                  type:"internal",
                  skills:c.skills||[],
                  utilization:c.utilizationPct||0,
                  availableFrom:c.availableFrom||null,
                  note:"",
                  // Financial planning fields (BigClaude Phase 3). Role defaults
                  // from the Consultant; hourlyRate pre-fills from billRate so
                  // internal rates don't have to be re-typed. allocatedHoursPerMonth
                  // stays blank until the PM commits to an allocation.
                  role:c.role||"Developer",
                  hourlyRate:typeof c.billRate==="number"&&c.billRate>0?c.billRate:null,
                  allocatedHoursPerMonth:null,
                };
                saveMatchedDevelopers(item.id,[...matched,dev]);
              }
              function removeMatch(devId){
                saveMatchedDevelopers(item.id,matched.filter(m=>m.id!==devId));
              }
              function updateMatchedDev(devId,patch){
                saveMatchedDevelopers(item.id,matched.map(m=>m.id===devId?{...m,...patch}:m));
              }
              function addExternalDev(){
                if(!extDevForm.name.trim()) return;
                const dev={
                  id:`ext_${uid()}`,
                  name:extDevForm.name.trim(),
                  type:"external",
                  skills:extDevForm.skills.split(",").map(s=>s.trim()).filter(Boolean),
                  availableFrom:extDevForm.availableFrom||null,
                  note:extDevForm.note||"",
                  cvName:extDevForm.cvName||null,
                  cvDataUrl:extDevForm.cvDataUrl||null,
                  // Financial planning fields — externals have no Consultant record
                  // to pre-fill from, so the PM fills these in after adding.
                  role:"Developer",
                  hourlyRate:null,
                  allocatedHoursPerMonth:null,
                };
                saveMatchedDevelopers(item.id,[...matched,dev]);
                setExtDevForm({name:"",skills:"",availableFrom:"",note:"",cvName:"",cvDataUrl:""});
                setShowExternalForm(false);
              }
              return (
                <div className="mt-5">
                  <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Developer Matching{matched.length>0&&<span className="kb-dev-count">{matched.length} matched</span>}</div>

                  {/* Matched developer cards */}
                  {matched.length>0&&(
                    <div className="kb-dev-list">
                      {matched.map(dev=>{
                        const isExt=dev.type==="external";
                        const utilCol=dev.utilization>=80?C.red:dev.utilization>=50?C.yellow:C.green;
                        return (
                          <div key={dev.id} className="kb-dev-card">
                            <div className="kb-dev-card-top">
                              <div className="kb-dev-avt"><Avt initials={getInit(dev.name)} size={32}/></div>
                              <div className="kb-dev-info">
                                <div className="kb-dev-name">{dev.name}
                                  {isExt&&<span className="kb-dev-badge ext">External</span>}
                                </div>
                                <div className="kb-dev-meta">
                                  {!isExt&&<span style={{color:utilCol,fontSize:10}}>{dev.utilization}% util</span>}
                                  {dev.availableFrom&&<span style={{fontSize:10,color:C.dim,marginLeft:isExt?0:8}}>Avail: {dev.availableFrom}</span>}
                                </div>
                              </div>
                              <button className="kb-dev-remove" title="Remove" onClick={()=>removeMatch(dev.id)}>×</button>
                            </div>
                            {dev.skills?.length>0&&(
                              <div className="kb-dev-skills">
                                {dev.skills.slice(0,6).map(sk=>(
                                  <span key={sk} className="kb-dev-skill-chip">{sk}</span>
                                ))}
                                {dev.skills.length>6&&<span className="kb-dev-skill-chip" style={{color:C.dim}}>+{dev.skills.length-6}</span>}
                              </div>
                            )}
                            {dev.note&&<div className="kb-dev-note">{dev.note}</div>}
                            {dev.cvDataUrl&&(
                              <div className="kb-dev-cv-link">
                                <a href={dev.cvDataUrl} download={dev.cvName||"cv"}>📎 {dev.cvName||"Download CV"}</a>
                              </div>
                            )}
                            {/* Financial planning row — per-resource role/rate/hours.
                                Rate is blank (not 0) when unset so the cost rollup
                                falls back to Scoping.hourlyRate. */}
                            <div style={{display:"grid",gridTemplateColumns:"1fr 90px 90px",gap:6,marginTop:8,fontSize:11}}>
                              <div>
                                <div style={{fontSize:9,color:C.dim,marginBottom:2,textTransform:"uppercase",letterSpacing:".08em"}}>Role</div>
                                <select
                                  value={dev.role||""}
                                  onChange={e=>updateMatchedDev(dev.id,{role:e.target.value||null})}
                                  style={{width:"100%",background:"#0a1528",border:"1px solid #14213d",color:"#dce8f8",borderRadius:5,padding:"3px 6px",fontSize:11}}
                                >
                                  <option value="">— Role —</option>
                                  {["Developer","PM","Designer","Architect","QA","DevOps","Other"].map(r=>(
                                    <option key={r} value={r}>{r}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <div style={{fontSize:9,color:C.dim,marginBottom:2,textTransform:"uppercase",letterSpacing:".08em"}}>Rate</div>
                                <input
                                  type="number" min="0" step="1"
                                  defaultValue={dev.hourlyRate??""}
                                  placeholder="fallback"
                                  onBlur={e=>{
                                    const raw=e.target.value;
                                    const next=raw===""?null:Math.round(Number(raw));
                                    if(next!==dev.hourlyRate) updateMatchedDev(dev.id,{hourlyRate:next});
                                  }}
                                  style={{width:"100%",background:"#0a1528",border:"1px solid #14213d",color:"#dce8f8",borderRadius:5,padding:"3px 6px",fontSize:11,fontFamily:"'Noto Sans Mono',monospace"}}
                                />
                              </div>
                              <div>
                                <div style={{fontSize:9,color:C.dim,marginBottom:2,textTransform:"uppercase",letterSpacing:".08em"}}>Hrs/mo</div>
                                <input
                                  type="number" min="0" step="1"
                                  defaultValue={dev.allocatedHoursPerMonth??""}
                                  placeholder="—"
                                  onBlur={e=>{
                                    const raw=e.target.value;
                                    const next=raw===""?null:Number(raw);
                                    if(next!==dev.allocatedHoursPerMonth) updateMatchedDev(dev.id,{allocatedHoursPerMonth:next});
                                  }}
                                  style={{width:"100%",background:"#0a1528",border:"1px solid #14213d",color:"#dce8f8",borderRadius:5,padding:"3px 6px",fontSize:11,fontFamily:"'Noto Sans Mono',monospace"}}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Consultant search */}
                  <div className="kb-dev-search-row">
                    <input className="kb-dev-search" placeholder="Search consultants by name or skill…"
                      value={devMatchSearch} onChange={e=>setDevMatchSearch(e.target.value)}/>
                    <button className="kb-dev-ext-btn" onClick={()=>setShowExternalForm(v=>!v)}>
                      {showExternalForm?"✕ Cancel":"+ External"}
                    </button>
                  </div>

                  {/* Consultant results */}
                  {devMatchSearch&&filteredConsultants.length>0&&(
                    <div className="kb-dev-results">
                      {filteredConsultants.slice(0,8).map(c=>{
                        const already=matched.some(m=>m.id===c.consultantId);
                        const utilCol=c.utilizationPct>=80?C.red:c.utilizationPct>=50?C.yellow:C.green;
                        return (
                          <div key={c.consultantId} className="kb-dev-result-row">
                            <Avt initials={getInit(c.name)} size={24}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:12,fontWeight:600}}>{c.name}</div>
                              <div style={{fontSize:10,color:C.dim}}>{c.role}</div>
                            </div>
                            <span style={{fontSize:10,color:utilCol,minWidth:40,textAlign:"right"}}>{c.utilizationPct}%</span>
                            {c.availableFrom&&<span style={{fontSize:10,color:C.dim,marginLeft:6}}>Avail: {c.availableFrom?.slice(0,10)}</span>}
                            <button className="kb-dev-add-btn" disabled={already} onClick={()=>addConsultantMatch(c)}>
                              {already?"✓":"+ Add"}
                            </button>
                          </div>
                        );
                      })}
                      {filteredConsultants.length>8&&<div style={{fontSize:10,color:C.dim,padding:"4px 0"}}>…{filteredConsultants.length-8} more — refine your search</div>}
                    </div>
                  )}
                  {devMatchSearch&&filteredConsultants.length===0&&(
                    <div style={{fontSize:11,color:C.dim,padding:"6px 0"}}>No consultants match "{devMatchSearch}"</div>
                  )}

                  {/* External developer form */}
                  {showExternalForm&&(
                    <div className="kb-ext-dev-form">
                      <div className="kb-section-title" style={{marginBottom:8}}>Add External Developer</div>
                      <input className="kb-ext-input" placeholder="Full name *" value={extDevForm.name}
                        onChange={e=>setExtDevForm(f=>({...f,name:e.target.value}))}/>
                      <input className="kb-ext-input" placeholder="Skills (comma-separated)"
                        value={extDevForm.skills} onChange={e=>setExtDevForm(f=>({...f,skills:e.target.value}))}/>
                      <input type="date" className="kb-ext-input" title="Available from"
                        value={extDevForm.availableFrom} onChange={e=>setExtDevForm(f=>({...f,availableFrom:e.target.value}))}/>
                      <textarea className="kb-ext-textarea" rows={2} placeholder="Note (optional)"
                        value={extDevForm.note} onChange={e=>setExtDevForm(f=>({...f,note:e.target.value}))}/>
                      <label className="kb-ext-file-label">
                        <input type="file" accept=".pdf,image/*" style={{display:"none"}}
                          onChange={e=>{
                            const f=e.target.files?.[0];
                            if(!f) return;
                            const reader=new FileReader();
                            reader.onload=ev=>setExtDevForm(prev=>({...prev,cvName:f.name,cvDataUrl:ev.target.result}));
                            reader.readAsDataURL(f);
                          }}/>
                        {extDevForm.cvName?<span style={{color:"var(--rc-accent-strong)"}}>📎 {extDevForm.cvName}</span>:<span>📎 Upload CV (PDF or image)</span>}
                      </label>
                      <button className="kb-dev-add-btn" style={{alignSelf:"flex-start",marginTop:4}}
                        onClick={addExternalDev}>Add External Developer</button>
                    </div>
                  )}

                  {/* Match notes */}
                  {item.matching?.matchNotes&&(
                    <div className="kb-dev-match-notes">
                      <div style={{fontSize:10,color:C.dim,marginBottom:4}}>Match Notes</div>
                      <div style={{fontSize:12,color:C.sub,lineHeight:1.5}}>{item.matching.matchNotes}</div>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="mt-5">
              <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Actions</div>
              <div className="flex gap-2 flex-wrap">
                <button className="kb-edit-btn" onClick={()=>openEditModal(item)}>Edit Details</button>
                <button
                  className="kb-edit-btn"
                  style={{background:"rgba(245,158,11,.12)",borderColor:"#f59e0b55",color:"#f59e0b"}}
                  title="Plan rates, budget hours, and resource costs for this engagement"
                  onClick={()=>setFinancialsOpenId(item.id)}
                >💰 Financials</button>
                {item.stage==="7. Done" && (
                  <button
                    className="kb-edit-btn"
                    style={{background:"rgba(16,185,129,.12)",borderColor:"#10b98155",color:"#10b981"}}
                    title="Create a Delivery project + client from this scoping. Assignments are seeded from matched developers."
                    onClick={async ()=>{
                      const defaultClient=item.name||"";
                      const clientName=window.prompt(
                        "Client name for the new project (press Cancel to abort):",
                        defaultClient,
                      );
                      if(clientName===null) return;
                      if(!clientName.trim()){ showToast?.("Error","Client name is required"); return; }
                      const projectName=window.prompt("Project name:", item.name||clientName) || clientName;
                      try{
                        const result=await apiClient.convertScopingToProject(item.id,{clientName,projectName});
                        showToast?.(
                          "Project created",
                          `${projectName} · ${result.assignmentCount} consultant${result.assignmentCount===1?"":"s"} assigned${result.clientCreated?" · new client":""}`,
                        );
                        if(!result.clientTrackerLink){
                          showToast?.("Heads up","New client has no tracker link yet — tracker push will wait.");
                        }
                        setSelectedCard(null);
                        nav?.("project", result.projectId);
                      }catch(err){
                        const msg=err?.response?.data?.error||err?.message||"Conversion failed";
                        showToast?.("Error", msg);
                      }
                    }}
                  >→ Create Project + Client</button>
                )}
                <button className="kb-delete-btn" onClick={()=>{ if(window.confirm(`Delete "${item.name}"?`)) deleteScopingItem(item.id,item.name); }}>Delete</button>
              </div>
            </div>
              </>
            )}

            {/* Tasks */}
            {drawerTab==="tasks" && (
            <div className="mt-5">
              <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Tasks ({tasks.filter(t=>t.done).length}/{tasks.length})</div>
              {tasks.map(t=>{
                const isExpanded=expandedTaskId===t.id;
                const comments=Array.isArray(t.comments)?t.comments:[];
                const attachments=Array.isArray(t.attachments)?t.attachments:[];
                const subtasks=Array.isArray(t.subtasks)?t.subtasks:[];
                const subtasksDone=subtasks.filter(s=>s.done).length;
                return (
                  <div key={t.id} className={`kb-task-card${isExpanded?" expanded":""}`}>
                    <div className="kb-task-row">
                      <div className={`kb-task-check${t.done?" done":""}`} onClick={()=>toggleTask(item.id,t.id)}>
                        {t.done&&<span style={{color:"#fff",fontSize:10,lineHeight:1}}>✓</span>}
                      </div>
                      <button className={`kb-task-label-btn${t.done?" done":""}`} onClick={()=>setExpandedTaskId(prev=>prev===t.id?null:t.id)}>
                        {t.title}
                      </button>
                      <select className="kb-assignee-sel" value={t.assigneeId||""} onChange={e=>assignTask(t.id,e.target.value||null)}>
                        <option value="">Assign…</option>
                        {activeUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                      <input type="date" className="kb-due-input" title="Due date"
                        value={t.dueDate||""} onChange={e=>setTaskDueDate(t.id,e.target.value||null)}/>
                      <span className="kb-task-meta">
                        {subtasks.length>0&&<span style={{marginRight:6}}>{subtasksDone}/{subtasks.length} sub</span>}
                        {comments.length}💬
                      </span>
                      <button className="kb-task-remove" onClick={()=>removeTask(item.id,t.id)}>×</button>
                    </div>
                    {isExpanded&&(
                      <div className="kb-task-detail">

                        {/* ── Subtasks ── */}
                        <div className="kb-task-section">
                          <div className="kb-task-section-title">Subtasks {subtasks.length>0&&`(${subtasksDone}/${subtasks.length})`}</div>
                          {subtasks.map(st=>{
                            const stExpanded=expandedSubtaskId===`${t.id}-${st.id}`;
                            const stComments=Array.isArray(st.comments)?st.comments:[];
                            return(
                            <div key={st.id} className="kb-subtask-card">
                              <div className="kb-subtask-row">
                                <div className={`kb-task-check${st.done?" done":""}`} style={{width:14,height:14,flexShrink:0}}
                                  onClick={()=>toggleSubtask(st.id,st.done)}>
                                  {st.done&&<span style={{color:"#fff",fontSize:8,lineHeight:1}}>✓</span>}
                                </div>
                                <button className={`kb-subtask-title${st.done?" done":""}`}
                                  style={{background:"none",border:"none",padding:0,cursor:"pointer",textAlign:"left",flex:1,font:"inherit",color:"inherit"}}
                                  onClick={()=>setExpandedSubtaskId(prev=>prev===`${t.id}-${st.id}`?null:`${t.id}-${st.id}`)}>
                                  {st.title}
                                </button>
                                <select className="kb-assignee-sel" style={{fontSize:"10px"}} value={st.assigneeId||""}
                                  onChange={e=>assignSubtask(st.id,e.target.value||null)}>
                                  <option value="">Assign…</option>
                                  {activeUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                                <input type="date" className="kb-due-input" style={{fontSize:"10px"}} title="Due date"
                                  value={st.dueDate||""} onChange={e=>setSubtaskDueDate(st.id,e.target.value||null)}/>
                                {stComments.length>0&&<span style={{fontSize:10,color:"#4a6a96",whiteSpace:"nowrap"}}>💬{stComments.length}</span>}
                                <button className="kb-task-file-remove" onClick={()=>removeSubtask(st.id)}>×</button>
                              </div>
                              {/* Subtask comments */}
                              {stExpanded&&(
                                <div className="kb-subtask-comments">
                                  {/* Subtask attachments */}
                                  {Array.isArray(st.attachments)&&st.attachments.length>0&&(
                                    <div className="kb-attach-list" style={{marginBottom:6}}>
                                      {st.attachments.map(a=>(
                                        <div key={a.id} className="kb-attach-chip">
                                          <a href={a.fileUrl} target="_blank" rel="noreferrer" className="kb-attach-name">{a.fileName}</a>
                                          <button className="kb-attach-del" title="Remove" onClick={()=>removeSubtaskAttachmentFile(a.id)}>×</button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {stComments.length===0&&<div className="kb-task-empty">No updates yet.</div>}
                              {stComments.map(c=>(
                                    <div key={c.id} className="aud-comment-row kb-comment-row">
                                      <div className="aud-comment-header">
                                        <span className="aud-comment-author">{c.author?.name||"Team"}</span>
                                        <span className="aud-comment-ts">{c.createdAt?relTime(c.createdAt):""}</span>
                                      </div>
                                      <CommentContent value={c.content} className="aud-comment-text rc-comment-body" />
                                    </div>
                                  ))}
                                  <div className="kb-update-box">
                                    <RichCommentInput
                                      rows={3}
                                      value={subtaskCommentDrafts[st.id] || ""}
                                      onChange={(html)=>setSubtaskCommentDrafts(prev=>({ ...prev, [st.id]: html }))}
                                      placeholder="Write an update…"
                                      onSubmit={({ html, mentions })=>addSubtaskComment(st.id, { content: html, mentions })}
                                      users={activeUsers}
                                      teams={teams}
                                      mentionScopeLabel="subtask"
                                      submitLabel="Post"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );})}
                          <div className="kb-subtask-add-row">
                            <input
                              className="kb-subtask-input"
                              placeholder="Add a subtask… (Enter to save)"
                              onKeyDown={e=>{
                                if(e.key==="Enter"&&e.target.value.trim()){
                                  addSubtask(t.id,e.target.value.trim());
                                  e.target.value="";
                                }
                              }}
                            />
                          </div>
                        </div>

                        {/* ── Attachments ── */}
                        {attachments.length>0&&(
                          <div className="kb-task-section">
                            <div className="kb-task-section-title">📎 Files ({attachments.length})</div>
                            <div className="kb-attach-list">
                              {attachments.map(a=>(
                                <div key={a.id} className="kb-attach-chip">
                                  <a href={a.fileUrl} target="_blank" rel="noreferrer" className="kb-attach-name">{a.fileName}</a>
                                  <button className="kb-attach-del" title="Remove" onClick={()=>removeTaskAttachmentFile(a.id)}>×</button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ── Comments ── */}
                        <div className="kb-task-section">
                          <div className="kb-task-section-title">💬 Updates ({comments.length})</div>
                          {comments.length===0&&<div className="kb-task-empty">No updates yet.</div>}
                          {comments.map(comment=>(
                            <div key={comment.id} className="aud-comment-row kb-comment-row">
                              <div className="aud-comment-header">
                                <span className="aud-comment-author">{comment.author?.name||"Team"}</span>
                                <span className="aud-comment-ts">{comment.createdAt ? relTime(comment.createdAt) : ""}</span>
                              </div>
                              <CommentContent value={comment.content} className="aud-comment-text rc-comment-body" />
                            </div>
                          ))}
                          <div className="kb-update-box">
                            <RichCommentInput
                              rows={4}
                              value={taskCommentDrafts[t.id] || ""}
                              onChange={(html)=>setTaskCommentDrafts(prev=>({ ...prev, [t.id]: html }))}
                              placeholder="Write an update…"
                              onSubmit={({ html, mentions })=>addTaskComment(t.id,{ content: html, mentions })}
                              users={activeUsers}
                              teams={teams}
                              mentionScopeLabel="task"
                              submitLabel="Post"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="kb-new-task-row">
                <input placeholder={`Add task (auto-assigned to ${stInfo.defaultAssignee||"team"})…`}
                  value={newTaskText} onChange={e=>setNewTaskText(e.target.value)}
                  onKeyDown={e=>{if(e.key==="Enter"){addTask(item.id,newTaskText);}}}/>
                <button onClick={()=>addTask(item.id,newTaskText)}>Add</button>
              </div>
            </div>
            )}

            {/* Developer Matching notes */}
            {drawerTab==="history" && (
              <div className="mt-5">
                <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Stage History</div>
                <Timeline items={history.map((h,i)=>{
                  const isCurrent = i === history.length - 1 && !h.completedAt;
                  const isCompleted = !!h.completedAt;
                  const metaParts = [];
                  if (h.enteredAt) metaParts.push(`Entered ${h.enteredAt}`);
                  if (h.completedAt) metaParts.push(`Completed ${h.completedAt}`);
                  return {
                    title: h.stage,
                    meta: metaParts.join(" → ") || "—",
                    body: h.by ? `By ${h.by}` : undefined,
                    tone: isCurrent ? "active" : isCompleted ? "done" : "default",
                  };
                })} />
              </div>
            )}

            {drawerTab==="history" && item.matching?.matchNotes&&(
              <div className="mt-5">
                <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Matching Notes</div>
                <div className="kb-match-notes">{item.matching.matchNotes}</div>
              </div>
            )}

            {drawerTab==="comments" && (
              <div className="mt-5">
                <div className="flex items-center gap-1.5 mb-2.5 text-[10px] font-bold uppercase tracking-[0.05em] text-[#4a6a96]">Comments ({scopingComments.length})</div>
                <div className="aud-comment-list">
                  {scopingComments.length===0 && <p className="text-[12px] text-[#4a6a96] py-5 text-center">No comments yet.</p>}
                  {scopingComments.map(comment=>(
                    <div key={comment.id} className="aud-comment-row">
                      <div className="aud-comment-header">
                        <span className="aud-comment-author">{comment.author?.name||"Team"}</span>
                        <span className="aud-comment-ts">{comment.createdAt ? relTime(comment.createdAt) : ""}</span>
                        <span className="sc-update-task-ref">· {comment.taskTitle}</span>
                      </div>
                      <CommentContent value={comment.content} className="aud-comment-text rc-comment-body" />
                    </div>
                  ))}
                </div>
                <div className="aud-comment-compose">
                  <RichCommentInput
                    rows={3}
                    value={scopingCommentDraft}
                    onChange={setScopingCommentDraft}
                    placeholder="Add a comment…"
                    users={activeUsers}
                    teams={teams}
                    mentionScopeLabel="scope"
                    submitLabel="Post"
                    collapsible
                    onSubmit={async ({ html, mentions }) => {
                      const anchorTask = tasks[0];
                      if(!anchorTask){
                        showToast?.("No tasks","Add a task first to post a comment");
                        return;
                      }
                      await addTaskComment(anchorTask.id, {
                        content: html,
                        mentions,
                        sourceType: "SCOPING",
                        sourceId: item.id,
                      });
                      setScopingCommentDraft("");
                    }}
                  />
                </div>
              </div>
            )}
          </SheetPrimitives.SheetBody>
        </SheetPrimitives.SheetContent>
      </SheetPrimitives.Sheet>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Table View — Monday.com-style list with expandable subitems
  // ─────────────────────────────────────────────────────────────────────────────
  function TableView(){
    const COL="minmax(200px,2fr) 64px 90px 110px 88px 64px 90px 80px 105px 72px 40px";
    const visibleStages=[...activeStages,...(showDoneLost?endStages:[])];
    return (
      <div className="sc-tbl-outer">
        <div className="sc-tbl-scroll">
          {/* Column header */}
          <div className="sc-tbl-head" style={{gridTemplateColumns:COL}}>
            {["Task","Owner","Priority","Status","Track","% Prob","Value","Month(s)","Earliest Due","Lead",""].map((h,i)=>(
              <div key={i} className={`sc-th${i===1||i===2||i===5||i===9||i===10?" sc-th-c":""}`}>{h}</div>
            ))}
          </div>

          {visibleStages.map(stage=>{
            const col=STAGE_COLORS[stage]||"#60a5fa";
            const cards=scoping.filter(s=>s.stage===stage).sort(sortCards);
            if(!cards.length)return null;
            const isOpen=expandedTableGroups[stage]!==false;
            return (
              <div key={stage} className="sc-tbl-group">
                {/* Stage group header */}
                <div className="sc-tbl-ghdr" style={{"--sc-col":col}}>
                  <button className="sc-tbl-gtoggle"
                    onClick={()=>setExpandedTableGroups(g=>({...g,[stage]:!isOpen}))}>
                    {isOpen?"▾":"▸"}
                  </button>
                  <span className="sc-tbl-gname">{stage.replace(/^\d\.\s/,"")}</span>
                  <span className="sc-tbl-gcnt" style={{background:col+"22",color:col}}>{cards.length}</span>
                </div>

                {isOpen && cards.map(item=>{
                  const isItemExpanded=expandedTableItems[item.id];
                  const isUpdateOpen=updatePanelId===item.id;
                  const tasks=item.tasks||[];
                  const pStyle=PRIORITY_STYLE[item.priority];
                  const totalComments=tasks.reduce((n,t)=>n+(t.comments?.length||0),0);
                  return (
                    <div key={item.id}>
                      {/* Item row */}
                      <div className={`sc-tbl-row sc-tbl-item${isUpdateOpen?" update-active":""}`}
                        style={{gridTemplateColumns:COL}}>
                        <div className="sc-td sc-td-task">
                          <button className="sc-tbl-expand" title="Toggle tasks"
                            onClick={()=>setExpandedTableItems(e=>({...e,[item.id]:!e[item.id]}))}>
                            {isItemExpanded?"▾":"▸"}
                          </button>
                          <span className="sc-tbl-name" onClick={()=>setSelectedCard(item)}>{item.name}</span>
                          {tasks.length>0&&<span className="sc-tbl-tcnt">{tasks.filter(t=>t.done).length}/{tasks.length}</span>}
                        </div>
                        <div className="sc-td sc-td-c">
                          {item.owner&&<Avt initials={getInit(item.owner)} size={22} title={item.owner}/>}
                        </div>
                        <div className="sc-td sc-td-c">
                          {item.priority&&pStyle
                            ?<span className="sc-prio-chip" style={{background:pStyle.bg,color:pStyle.fg}}>{item.priority}</span>
                            :<span className="sc-td-em">—</span>}
                        </div>
                        <div className="sc-td">
                          <span className="sc-status-chip" style={{background:col+"22",color:col}}>
                            {stage.replace(/^\d\.\s/,"")}
                          </span>
                        </div>
                        <div className="sc-td sc-td-txt">{item.track||<span className="sc-td-em">—</span>}</div>
                        <div className="sc-td sc-td-c sc-td-txt">{item.probability!=null?`${item.probability}%`:<span className="sc-td-em">—</span>}</div>
                        <div className="sc-td sc-td-r sc-td-txt">{item.dealValue?`${Number(item.dealValue).toLocaleString()} kr`:<span className="sc-td-em">—</span>}</div>
                        <div className="sc-td sc-td-txt">{item.months?.length?item.months.slice(0,2).join(", ")+(item.months.length>2?"…":""):<span className="sc-td-em">—</span>}</div>
                        <div className="sc-td sc-td-txt">{item.earliestDueDate||<span className="sc-td-em">—</span>}</div>
                        <div className="sc-td sc-td-c">
                          {item.leadActive
                            ?<span className="sc-lead-chip">{item.leadActive}</span>
                            :<span className="sc-td-em">—</span>}
                        </div>
                        <div className="sc-td sc-td-c">
                          <button className="sc-comment-btn" title="Updates"
                            onClick={()=>setUpdatePanelId(prev=>prev===item.id?null:item.id)}>
                            💬{totalComments>0&&<span className="sc-comment-cnt">{totalComments}</span>}
                          </button>
                        </div>
                      </div>

                      {/* Subitems / scoping tasks */}
                      {isItemExpanded&&(
                        <div className="sc-subitems-wrap">
                          <div className="sc-sub-hdr">
                            <div className="sc-sub-th sc-sub-th-name">Subitem</div>
                            <div className="sc-sub-th sc-sub-th-owner">Owner</div>
                            <div className="sc-sub-th sc-sub-th-status">Status</div>
                            <div className="sc-sub-th sc-sub-th-due">Due Date</div>
                            <div className="sc-sub-th"/>
                          </div>
                          {tasks.map(t=>(
                            <div key={t.id} className={`sc-sub-row${t.done?" done":""}`}>
                              <div className="sc-sub-td sc-sub-td-name">
                                <div className={`sc-sub-chk${t.done?" done":""}`}
                                  onClick={()=>toggleTask(item.id,t.id)}>
                                  {t.done&&"✓"}
                                </div>
                                <span className={`sc-sub-title${t.done?" done":""}`}>{t.title}</span>
                                {(t.subtasks?.length>0||t.comments?.length>0)&&(
                                  <span className="sc-sub-meta">
                                    {t.subtasks?.length>0&&<span>{t.subtasks.filter(s=>s.done).length}/{t.subtasks.length}</span>}
                                    {t.comments?.length>0&&<span>💬{t.comments.length}</span>}
                                  </span>
                                )}
                              </div>
                              <div className="sc-sub-td sc-sub-td-owner">
                                <select className="sc-sub-sel" value={t.assigneeId||""}
                                  onChange={e=>assignTask(t.id,e.target.value||null)}>
                                  <option value="">—</option>
                                  {activeUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                              </div>
                              <div className="sc-sub-td sc-sub-td-status">
                                <span className={`sc-sub-status${t.done?" done":""}`}>{t.done?"Done":"To Do"}</span>
                              </div>
                              <div className="sc-sub-td sc-sub-td-due">
                                <input type="date" className="sc-sub-date" value={t.dueDate||""}
                                  onChange={e=>setTaskDueDate(t.id,e.target.value||null)}/>
                              </div>
                              <div className="sc-sub-td sc-sub-td-del">
                                <button className="sc-sub-del" onClick={()=>removeTask(item.id,t.id)}>×</button>
                              </div>
                            </div>
                          ))}
                          <div className="sc-sub-add-row">
                            <input className="sc-sub-add-input" placeholder="+ Add task…"
                              onKeyDown={e=>{
                                if(e.key==="Enter"&&e.target.value.trim()){
                                  addTask(item.id,e.target.value.trim());
                                  e.target.value="";
                                }
                              }}/>
                          </div>
                        </div>
                      )}

                      {/* Updates panel */}
                      {isUpdateOpen&&(
                        <div className="sc-update-panel">
                          <div className="sc-update-tabs">
                            <span className="sc-update-tab sc-update-tab-active">Updates</span>
                            <span className="sc-update-tab">Files</span>
                            <span className="sc-update-tab">Activity Log</span>
                            <span className="sc-update-tab sc-update-tab-plus">+</span>
                          </div>
                          <div className="sc-update-links">
                            <span className="sc-update-link">✉ Update via email</span>
                            <span className="sc-update-sep">|</span>
                            <span className="sc-update-link">◎ Give feedback</span>
                          </div>
                          {/* Comments from all tasks */}
                          {tasks.flatMap(t=>(t.comments||[]).map(c=>({...c,taskTitle:t.title}))).map(c=>(
                            <div key={c.id} className="sc-update-comment">
                              <div className="sc-update-comment-head">
                                <Avt initials={getInit(c.author?.name||"T")} size={24}/>
                                <div style={{flex:1}}>
                                  <span className="sc-update-author">{c.author?.name||"Team"}</span>
                                  <span className="sc-update-task-ref"> · {c.taskTitle}</span>
                                  <span className="sc-update-time">{c.createdAt?relTime(c.createdAt):""}</span>
                                </div>
                              </div>
                              <CommentContent value={c.content} className="sc-update-comment-body rc-comment-body" />
                            </div>
                          ))}
                          <div className="sc-update-compose">
                            <RichCommentInput rows={4}
                              className="sc-update-rich"
                              placeholder="Write an update and mention others with @"
                              value={updatePanelText}
                              onChange={setUpdatePanelText}
                              users={activeUsers}
                              teams={teams}
                              mentionScopeLabel="board"
                              submitLabel="Update"
                              onSubmit={async({ html, mentions })=>{
                                const firstTask=tasks[0];
                                if(firstTask){
                                  try{
                                    const updated=await apiClient.addScopingTaskComment(firstTask.id,{
                                      content: html,
                                      mentions,
                                      sourceType: "SCOPING",
                                      sourceId: item.id,
                                    });
                                    upsertScopingLocal(mapScoping(updated),false);
                                    setUpdatePanelText("");
                                  }catch(err){console.error("Failed to post update",err);}
                                } else {
                                  showToast?.("No tasks","Add a task first to post an update");
                                }
                              }}/>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add item to this stage */}
                {!stage.includes("Done")&&!stage.includes("Lost")&&(
                  <div className="sc-tbl-add-row">
                    <button className="sc-tbl-add-btn" onClick={()=>openCreateModal(stage)}>+ Add task</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // BigClaude Phase 4: Financial planning modal. Overlays on top of the
  // Scoping drawer, reads the live scoping record from state, and writes
  // top-level fields via saveScopingFields (per-field on blur / change).
  // Resource-level rate/hours are the same JSON structure the Developer
  // Matching section uses; edits here call updateMatchedDev-equivalent
  // (saveMatchedDevelopers) so both surfaces stay coherent.
  function FinancialsModal(){
    if(!financialsOpenId) return null;
    const item = scoping.find(s => s.id === financialsOpenId);
    if(!item) return null;

    const resources = Array.isArray(item.matching?.matchedDevelopers) ? item.matching.matchedDevelopers : [];
    const months = monthsBetween(item.startDate, item.endDate);
    const budgetHours = computeBudgetHours(item.startDate, item.endDate, item.monthlyBudgetHours);
    const projectBudget = computeProjectBudget(budgetHours, item.hourlyRate);
    // For cost, convert each resource's per-month hours to total hours first.
    const resourceRows = resources.map(r => ({
      hourlyRate: r.hourlyRate,
      allocatedHours: (r.allocatedHoursPerMonth || 0) * months,
    }));
    const projectCost = computeProjectCost(resourceRows, item.hourlyRate);
    const marginPct = computeMarginPct(projectBudget, projectCost);

    const fmtMoney = (n) => {
      const sym = item.currency || "SEK";
      return `${Math.round(n || 0).toLocaleString()} ${sym}`;
    };

    function saveField(field, value){ saveScopingFields(item.id, { [field]: value }); }
    function updateResource(devId, patch){
      const next = resources.map(m => m.id === devId ? { ...m, ...patch } : m);
      saveMatchedDevelopers(item.id, next);
    }

    const close = () => setFinancialsOpenId(null);

    return (
      <div className="kb-modal-bg" onClick={e=>{if(e.target===e.currentTarget)close();}}>
        <div className="kb-modal" style={{maxWidth:820}}>
          <div className="kb-modal-header">
            <div className="kb-modal-title-wrap">
              <div className="kb-modal-title">💰 Financials — {item.name}</div>
              <div className="kb-modal-sub">Plan rates, budget, and per-resource costs. Carries into Project on convert.</div>
            </div>
            <button className="kb-drawer-close" onClick={close}>✕</button>
          </div>
          <div className="kb-modal-body">
            {/* ── Editable scoping-level inputs ────────────────────────────── */}
            <div className="kb-modal-grid">
              <div className="fgroup">
                <label className="kb-modal-label">Project Title</label>
                <input
                  className="kb-modal-input"
                  defaultValue={item.projectTitle||""}
                  placeholder={item.name}
                  onBlur={e=>{const v=e.target.value.trim()||null;if(v!==(item.projectTitle||null))saveField("projectTitle",v);}}
                />
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Currency</label>
                <select
                  className="kb-modal-input"
                  value={item.currency||"SEK"}
                  onChange={e=>saveField("currency",e.target.value)}
                >
                  {CURRENCY_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Hourly Rate (fallback)</label>
                <input
                  className="kb-modal-input"
                  type="number" min="0" step="1"
                  defaultValue={item.hourlyRate??""}
                  placeholder="e.g. 1250"
                  onBlur={e=>{
                    const raw=e.target.value;
                    const next=raw===""?null:Math.round(Number(raw));
                    if(next!==item.hourlyRate) saveField("hourlyRate",next);
                  }}
                />
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Monthly Budget Hours</label>
                <input
                  className="kb-modal-input"
                  type="number" min="0" step="1"
                  defaultValue={item.monthlyBudgetHours??""}
                  placeholder="e.g. 160"
                  onBlur={e=>{
                    const raw=e.target.value;
                    const next=raw===""?null:Number(raw);
                    if(next!==item.monthlyBudgetHours) saveField("monthlyBudgetHours",next);
                  }}
                />
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Start Date</label>
                <input
                  className="kb-modal-input"
                  type="date"
                  defaultValue={item.startDate||""}
                  onBlur={e=>{const v=e.target.value||null;if(v!==(item.startDate||null))saveField("startDate",v);}}
                />
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">End Date</label>
                <input
                  className="kb-modal-input"
                  type="date"
                  defaultValue={item.endDate||""}
                  onBlur={e=>{const v=e.target.value||null;if(v!==(item.endDate||null))saveField("endDate",v);}}
                />
              </div>
            </div>

            {/* ── Derived strip ─────────────────────────────────────────────── */}
            <div style={{
              marginTop:18, padding:"12px 14px", borderRadius:8,
              background:"#0a1528", border:"1px solid #14213d",
              display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:12,
            }}>
              {[
                ["Duration", `${months.toFixed(2)} months`, "#60a5fa"],
                ["Budget Hours", `${budgetHours.toLocaleString()} h`, "#a78bfa"],
                ["Project Budget", fmtMoney(projectBudget), "#10c981"],
                ["Project Cost", fmtMoney(projectCost), "#f97316"],
                ["Margin", marginPct==null ? "—" : `${marginPct.toFixed(1)}%`,
                  marginPct==null ? C.dim : marginPct>=30 ? "#10c981" : marginPct>=10 ? "#f59e0b" : "#ef4444"],
              ].map(([label,value,col])=>(
                <div key={label}>
                  <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>{label}</div>
                  <div style={{fontSize:14,fontWeight:700,color:col,fontFamily:"'Noto Sans Mono',monospace"}}>{value}</div>
                </div>
              ))}
            </div>

            {/* ── Resources table ──────────────────────────────────────────── */}
            <div style={{marginTop:18}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div className="kb-section-title">Resources ({resources.length})</div>
                {resources.length===0 && (
                  <span style={{fontSize:11,color:C.dim}}>Add developers in the Developer Matching section first.</span>
                )}
              </div>
              {resources.length>0 && (
                <div style={{border:"1px solid #14213d",borderRadius:8,overflow:"hidden"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1.4fr 1.6fr 110px 100px 120px",gap:10,padding:"8px 12px",background:"#0a1528",fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:".08em"}}>
                    <div>Role</div><div>Consultant</div><div style={{textAlign:"right"}}>Rate</div><div style={{textAlign:"right"}}>Hrs/mo</div><div style={{textAlign:"right"}}>Total</div>
                  </div>
                  {resources.map(r=>{
                    const rate = (r.hourlyRate && r.hourlyRate > 0) ? r.hourlyRate : (item.hourlyRate || 0);
                    const totalHrs = (r.allocatedHoursPerMonth || 0) * months;
                    const rowCost = totalHrs * rate;
                    const rateIsFallback = !(r.hourlyRate > 0);
                    return (
                      <div key={r.id} style={{display:"grid",gridTemplateColumns:"1.4fr 1.6fr 110px 100px 120px",gap:10,padding:"8px 12px",borderTop:"1px solid #14213d",alignItems:"center",fontSize:12}}>
                        <select
                          value={r.role||""}
                          onChange={e=>updateResource(r.id,{role:e.target.value||null})}
                          style={{background:"#0a1528",border:"1px solid #14213d",color:"#dce8f8",borderRadius:5,padding:"3px 6px",fontSize:12}}
                        >
                          <option value="">— Role —</option>
                          {["Developer","PM","Designer","Architect","QA","DevOps","Other"].map(x=><option key={x} value={x}>{x}</option>)}
                        </select>
                        <div style={{minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {r.name}{r.type==="external" && <span style={{marginLeft:6,fontSize:9,color:C.yellow}}>EXT</span>}
                        </div>
                        <input
                          type="number" min="0" step="1"
                          defaultValue={r.hourlyRate??""}
                          placeholder="fallback"
                          onBlur={e=>{const raw=e.target.value;const next=raw===""?null:Math.round(Number(raw));if(next!==r.hourlyRate)updateResource(r.id,{hourlyRate:next});}}
                          style={{background:"#0a1528",border:"1px solid #14213d",color:"#dce8f8",borderRadius:5,padding:"3px 6px",fontSize:12,textAlign:"right",fontFamily:"'Noto Sans Mono',monospace"}}
                        />
                        <input
                          type="number" min="0" step="1"
                          defaultValue={r.allocatedHoursPerMonth??""}
                          placeholder="—"
                          onBlur={e=>{const raw=e.target.value;const next=raw===""?null:Number(raw);if(next!==r.allocatedHoursPerMonth)updateResource(r.id,{allocatedHoursPerMonth:next});}}
                          style={{background:"#0a1528",border:"1px solid #14213d",color:"#dce8f8",borderRadius:5,padding:"3px 6px",fontSize:12,textAlign:"right",fontFamily:"'Noto Sans Mono',monospace"}}
                        />
                        <div style={{textAlign:"right",color:rowCost>0?C.text:C.dim,fontFamily:"'Noto Sans Mono',monospace"}}>
                          {rowCost>0 ? fmtMoney(rowCost) : "—"}
                          {rateIsFallback && rate>0 && <div style={{fontSize:9,color:C.dim,fontWeight:400}}>fallback rate</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Capacity variance hint — non-blocking */}
              {budgetHours>0 && resources.length>0 && (() => {
                const allocatedTotal = resources.reduce((s,r)=>s+((r.allocatedHoursPerMonth||0)*months),0);
                if(allocatedTotal===0) return null;
                const delta = allocatedTotal - budgetHours;
                const col = Math.abs(delta)<budgetHours*0.1 ? C.green : C.yellow;
                return (
                  <div style={{marginTop:10,fontSize:11,color:col}}>
                    📊 Allocated: {Math.round(allocatedTotal).toLocaleString()} h vs contracted {budgetHours.toLocaleString()} h
                    {delta>0 ? ` · over-allocated by ${Math.round(delta).toLocaleString()} h` : delta<0 ? ` · ${Math.round(-delta).toLocaleString()} h slack` : " · perfectly aligned"}
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="kb-modal-actions">
            <button className="kb-modal-save" onClick={close}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  function EditorModal(){
    if(!editorOpen||!editorForm)return null;
    return (
      <div className="kb-modal-bg" onClick={e=>{if(e.target===e.currentTarget)closeEditor();}}>
        <div className="kb-modal" onKeyDown={e=>{
            if(e.key==="Enter"){
              const target=e.target;
              const isTextArea=target.nodeName==="TEXTAREA";
              const isTaskRow=target.closest(".kb-new-task-row");
              if(!isTextArea && !isTaskRow){
                e.preventDefault();
              }
            }
          }}>
          <div className="kb-modal-head">
            <div>
              <div className="kb-modal-title">{editorMode==="create"?"Add Scoping Item":"Edit Scoping Item"}</div>
              <div className="kb-modal-sub">Capture the scoping details without leaving the board.</div>
            </div>
            <button className="kb-drawer-close" onClick={closeEditor}>✕</button>
          </div>
          <div className="kb-modal-body">
            <div className="kb-modal-grid">
              <div className="fgroup">
                <label className="kb-modal-label">Name <span style={{color:"#ef4444"}}>*</span></label>
                <input className={`kb-modal-input${hasEditorError("name")?" err":""}`} value={editorForm.name} onChange={e=>setEditorField("name",e.target.value)} placeholder="Client or opportunity name"/>
                {hasEditorError("name") && <span style={{color:"#ef4444",fontSize:11,marginTop:4}}>⚠ {hasEditorError("name")}</span>}
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Owner <span style={{color:"#ef4444"}}>*</span></label>
                <select className={`kb-modal-input${hasEditorError("ownerId")?" err":""}`} value={editorForm.ownerId||""} onChange={e=>{const id=e.target.value; const u=usersMap[id]; setEditorField("ownerId",id); setEditorField("owner",u?.name||"");}}>
                  <option value="">— Select owner —</option>
                  {activeUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                {hasEditorError("ownerId") && <span style={{color:"#ef4444",fontSize:11,marginTop:4}}>⚠ {hasEditorError("ownerId")}</span>}
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Stage <span style={{color:"#ef4444"}}>*</span></label>
                <select className={`kb-modal-input${hasEditorError("stage")?" err":""}`} value={editorForm.stage} onChange={e=>setEditorField("stage",e.target.value)}>
                  {SCOPING_STAGES.map(stage=><option key={stage} value={stage}>{stage}</option>)}
                </select>
                {hasEditorError("stage") && <span style={{color:"#ef4444",fontSize:11,marginTop:4}}>⚠ {hasEditorError("stage")}</span>}
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Priority</label>
                <select className="kb-modal-input" value={editorForm.priority} onChange={e=>setEditorField("priority",e.target.value)}>
                  <option value="">None</option>
                  {["Critical","High","Medium","Low"].map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Track</label>
                <select className="kb-modal-input" value={editorForm.track} onChange={e=>setEditorField("track",e.target.value)}>
                  <option value="">Select track</option>
                  {["Resource","Project","Growth Task"].map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Lead / Active</label>
                <select className="kb-modal-input" value={editorForm.leadActive} onChange={e=>setEditorField("leadActive",e.target.value)}>
                  <option value="">Not set</option>
                  {["Lead","Active"].map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Probability %</label>
                <input className="kb-modal-input" type="number" min="0" max="100" value={editorForm.probability} onChange={e=>setEditorField("probability",e.target.value)} placeholder="0-100"/>
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Deal Value</label>
                <input className="kb-modal-input" type="number" min="0" value={editorForm.dealValue} onChange={e=>setEditorField("dealValue",e.target.value)} placeholder="EUR amount"/>
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Start Date <span style={{color:"#ef4444"}}>*</span></label>
                <input className={`kb-modal-input${hasEditorError("startDate")?" err":""}`} type="date" value={editorForm.startDate} onChange={e=>setEditorField("startDate",e.target.value)}/>
                {hasEditorError("startDate") && <span style={{color:"#ef4444",fontSize:11,marginTop:4}}>⚠ {hasEditorError("startDate")}</span>}
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">End Date <span style={{color:"#ef4444"}}>*</span></label>
                <input className={`kb-modal-input${hasEditorError("endDate")?" err":""}`} type="date" value={editorForm.endDate} onChange={e=>setEditorField("endDate",e.target.value)}/>
                {hasEditorError("endDate") && <span style={{color:"#ef4444",fontSize:11,marginTop:4}}>⚠ {hasEditorError("endDate")}</span>}
              </div>
              <div className="fgroup">
                <label className="kb-modal-label">Earliest Due</label>
                <input className="kb-modal-input" type="date" value={editorForm.earliestDueDate} onChange={e=>setEditorField("earliestDueDate",e.target.value)}/>
              </div>
            </div>

            <div className="fgroup" style={{marginTop:18}}>
              <label className="kb-modal-label">Description</label>
              <textarea className="kb-modal-textarea" value={editorForm.description} onChange={e=>setEditorField("description",e.target.value)} placeholder="Context, scope, notes, and what this engagement needs."/>
            </div>

            <div className="kb-modal-grid kb-modal-grid-2" style={{marginTop:18}}>
              <div className="fgroup">
                <label className="kb-modal-label">Months</label>
                <div className="kb-check-grid">
                  {MONTH_OPTIONS.map(month=>(
                    <label key={month} className="kb-check-item">
                      <input type="checkbox" checked={editorForm.months.includes(month)} onChange={()=>toggleEditorListValue("months",month)}/>
                      <span>{month}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="fgroup">
                <label className="kb-modal-label">Collaborators</label>
                <div className="kb-check-grid">
                  {activeUsers.map(u=>(
                    <label key={u.id} className="kb-check-item">
                      <input type="checkbox" checked={editorForm.collaborators.includes(u.id)} onChange={()=>toggleEditorListValue("collaborators",u.id)}/>
                      <span>{u.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="fgroup" style={{marginTop:18}}>
              <label className="kb-modal-label">Matching Notes</label>
              <textarea className="kb-modal-textarea" value={editorForm.matchNotes||""} onChange={e=>setEditorField("matchNotes",e.target.value)} placeholder="Resource notes, candidate suggestions, or interview outcomes."/>
            </div>
          </div>
          <div className="kb-modal-actions">
            <button className="kb-modal-cancel" onClick={closeEditor}>Cancel</button>
            <button className="kb-modal-save" onClick={saveEditor}>{editorMode==="create"?"Create Item":"Save Changes"}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pg-body scoping-page">
      <div className="pg-top scoping-top scoping-top-space">
        <div className="scoping-top-row">
          <div>
            <div className="pg-title">Scoping Pipeline</div>
            <div className="pg-sub">Track scoping engagements through each stage · {viewMode==="kanban"?"Kanban":"Table"} view</div>
          </div>
          <div className="sc-top-actions" id="scoping-actions">
            <div className="sc-view-toggle">
              <button className={`sc-view-btn${viewMode==="kanban"?" active":""}`} onClick={()=>setViewMode("kanban")}>⊞ Kanban</button>
              <button className={`sc-view-btn${viewMode==="table"?" active":""}`} onClick={()=>setViewMode("table")}>☰ Table</button>
            </div>
            <label className="scoping-toggle" style={{cursor:"pointer"}}>
              <input className="scoping-toggle-input" type="checkbox" checked={showDoneLost} onChange={e=>setShowDoneLost(e.target.checked)}/>
              Show Done &amp; Lost
            </label>
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="krow scoping-kpis scoping-kpis-space">
        <div className="kpi scoping-kpi"><div className="kpi-lbl">Active</div><div className="kpi-val">{totalActive}</div></div>
        <div className="kpi scoping-kpi"><div className="kpi-lbl">Matching</div><div className="kpi-val kpi-val-matching">{inMatching}</div></div>
        <div className="kpi scoping-kpi"><div className="kpi-lbl">Won</div><div className="kpi-val kpi-val-won">{won}</div></div>
        <div className="kpi scoping-kpi"><div className="kpi-lbl">Lost</div><div className="kpi-val kpi-val-lost">{lost}</div></div>
      </div>

      {/* Kanban board or Table view */}
      {viewMode==="table" ? TableView() : <div className="kb-board">
        {[...activeStages,...(showDoneLost?endStages:[])].map(stage=>{
          const col=STAGE_COLORS[stage];
          const stInfo=SCOPING_STAGE_TEAM[stage]||{};
          const liveColTeam=getTeamForStage(stage);
          const colTeamName=liveColTeam?.name||stInfo.team||null;
          const colAssignee=liveColTeam?.members?.[0]?.user?.name||stInfo.defaultAssignee||null;
          const cards=scoping.filter(s=>s.stage===stage).sort(sortCards);
          return (
            <div key={stage} className="kb-col" style={{"--kb-stage-color":col,borderTopColor:col}}>
              <div className="kb-col-hdr">
                <div>
                  <div className="kb-col-title">
                    <span className="kb-stage-icon">{STAGE_ICONS[stage]}</span>
                    <span className="kb-stage-name">{stage.replace(/^\d\.\s/,"")}</span>
                    <span className="kb-col-count" style={{background:`${col}1f`,color:col,border:`1px solid ${col}33`}}>{cards.length}</span>
                  </div>
                  {(colTeamName||colAssignee)&&<div className="kb-col-team">{colTeamName?`${colTeamName} →`:""} {colAssignee||""}</div>}
                </div>
              </div>
              <div
                className={`kb-col-body${dragOverStage===stage?" drag-over":""}`}
                onDragOver={e=>{e.preventDefault();if(dragOverStage!==stage)setDragOverStage(stage);}}
                onDragLeave={e=>{if(!e.currentTarget.contains(e.relatedTarget))setDragOverStage(null);}}
                onDrop={e=>{
                  e.preventDefault();
                  const droppedId=e.dataTransfer.getData("text/plain");
                  setDraggingId(null);setDragOverStage(null);
                  if(droppedId) moveToStage(droppedId,stage);
                }}
              >
                {cards.map(item=><div key={item.id}>{KbCard({item})}</div>)}
                {draggingId&&dragOverStage===stage&&<div className="kb-drop-placeholder"/>}
                {!stage.includes("Done")&&!stage.includes("Lost")&&(
                  <button className="kb-add-btn" onClick={()=>openCreateModal(stage)}>+ Add to {stage.replace(/^\d\.\s/,"")}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>}

      {/* Detail drawer — called as functions to avoid React unmounting on parent re-render */}
      {DetailDrawer()}
      {EditorModal()}
      {FinancialsModal()}
    </div>
  );
}


